import React, { useState, useEffect } from 'react';
import RedeemCodeModal from './RedeemCodeModal';
import styles from './PurchaseDetailModal.module.scss';

const PurchaseDetailModal = ({ purchase, onClose, onRedeemed }) => {
    const [show, setShow] = useState(false);
    const [showRedeemModal, setShowRedeemModal] = useState(false);
    const [redeemCode, setRedeemCode] = useState('');

    useEffect(() => {
        // Trigger show animation after mount
        setTimeout(() => setShow(true), 10);
        // Lock body scroll
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleClose = () => {
        setShow(false);
        setTimeout(() => {
            onClose();
        }, 500);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return `£${amount.toFixed(2)}`;
    };

    if (!purchase) return null;

    return (
        <div className={`${styles.screenOverlay} ${show ? styles.show : ''}`}>
            <div className={styles.screenOverlayBG} onClick={handleClose}></div>
            <div className={styles.modalContainer} onClick={handleBackdropClick}>
                <div className={styles.modal}>
                    <button className={styles.closeButton} onClick={handleClose}>
                        &times;
                    </button>

                    <h1>Purchase Details</h1>

                    <div className={styles.content}>
                        <div className={styles.header}>
                            <div className={styles.headerRow}>
                                <span className={styles.headerLabel}>Date</span>
                                <span className={styles.headerValue}>{formatDate(purchase.date)}</span>
                            </div>
                            <div className={styles.headerRow}>
                                <span className={styles.headerLabel}>Order ID</span>
                                <span className={styles.headerValue}>{purchase.paymentIntentId}</span>
                            </div>
                        </div>

                        <div className={styles.itemsSection}>
                            <h3>Items</h3>
                            {purchase.items?.map((item, index) => (
                                <div key={index} className={styles.itemCard}>
                                    <div className={styles.itemHeader}>
                                        <span className={styles.itemName}>{item.name}</span>
                                        <span className={styles.itemPrice}>{formatCurrency(item.unitPrice - item.discountAmount)}</span>
                                    </div>
                                    {item.discountAmount > 0 && (
                                        <div className={styles.itemDiscount}>
                                            <span>Discount: -{formatCurrency(item.discountAmount)}</span>
                                        </div>
                                    )}
                                    <div className={styles.keySection}>
                                        <div className={styles.keyCode}>
                                            <span className={styles.keyLabel}>Key:</span>
                                            <code className={styles.keyValue}>{item.keyCode}</code>
                                        </div>
                                        <div className={styles.keyStatus}>
                                            {item.redeemed ? (
                                                <span className={styles.redeemedBadge}>
                                                    Redeemed by {item.redeemedByEmail || 'Unknown'}
                                                </span>
                                            ) : (
                                                <span
                                                    className={styles.redeemLink}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRedeemCode(item.keyCode);
                                                        setShowRedeemModal(true);
                                                    }}
                                                >
                                                    Activate Now &rarr;
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.summary}>
                            {purchase.discountCode && (
                                <div className={styles.summaryRow}>
                                    <span>Discount Code</span>
                                    <span className={styles.discountBadge}>{purchase.discountCode}</span>
                                </div>
                            )}
                            {purchase.discountTotal > 0 && (
                                <div className={styles.summaryRow}>
                                    <span>Total Discount</span>
                                    <span className={styles.discountAmount}>
                                        -{formatCurrency(purchase.discountTotal)}
                                    </span>
                                </div>
                            )}
                            <div className={`${styles.summaryRow} ${styles.total}`}>
                                <span>Total Paid</span>
                                <span>{formatCurrency(purchase.total - purchase.discountTotal)}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.buttons}>
                        <button className={`${styles.button} ${styles.small}`} onClick={handleClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {showRedeemModal && (
                <RedeemCodeModal
                    prefillCode={redeemCode}
                    onClose={(wasRedeemed) => {
                        setShowRedeemModal(false);
                        setRedeemCode('');
                        if (wasRedeemed && onRedeemed) {
                            onRedeemed();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default PurchaseDetailModal;
